import type { AddressValidationRulesData } from '@/lib/address/countryAreaRules';

interface ValidationRulesApiResponse {
  success: boolean;
  data?: AddressValidationRulesData;
}

const rulesCache = new Map<string, AddressValidationRulesData>();
const inflight = new Map<string, Promise<AddressValidationRulesData | null>>();

export function getCachedAddressValidationRules(
  countryCode: string
): AddressValidationRulesData | null {
  const normalized = countryCode.trim().toUpperCase();
  if (normalized.length !== 2) {
    return null;
  }

  return rulesCache.get(normalized) ?? null;
}

export function getAddressValidationRules(
  countryCode: string
): Promise<AddressValidationRulesData | null> {
  const normalized = countryCode.trim().toUpperCase();
  if (normalized.length !== 2) {
    return Promise.resolve(null);
  }

  const cached = rulesCache.get(normalized);
  if (cached) {
    return Promise.resolve(cached);
  }

  const pending = inflight.get(normalized);
  if (pending) {
    return pending;
  }

  const request = fetch(`/api/address/validation-rules?countryCode=${normalized}`)
    .then(async (response) => {
      const body = (await response.json()) as ValidationRulesApiResponse;
      if (body.success && body.data) {
        rulesCache.set(normalized, body.data);
        return body.data;
      }
      return null;
    })
    .finally(() => {
      inflight.delete(normalized);
    });

  inflight.set(normalized, request);
  return request;
}

export function clearAddressValidationRulesCache(): void {
  rulesCache.clear();
  inflight.clear();
}
